import * as i0 from '@angular/core';
import { InjectionToken, Component, ChangeDetectionStrategy, ViewChild, ElementRef, Injectable, Directive, Input, Pipe, EventEmitter, ViewEncapsulation, Output, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/router';
import { NavigationStart, NavigationEnd, RouterModule } from '@angular/router';
import * as i3$1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i2$1 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import * as i1 from '@angular/cdk/portal';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, PortalInjector, PortalModule } from '@angular/cdk/portal';
import { FsScrollModule } from '@firestitch/scroll';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay';
import * as i3 from '@angular/cdk/layout';
import { Breakpoints } from '@angular/cdk/layout';
import { Subject, Observable } from 'rxjs';
import { filter, tap, take, takeUntil } from 'rxjs/operators';

/** Injection token that can be used to access the data that was passed in to a drawer. */
const MENU_DATA = new InjectionToken('MenuData');

class SidenavMenuRef {
    constructor(_overlayRef, router, panelClass) {
        this._overlayRef = _overlayRef;
        this.router = router;
        /** Subject for notifying the user that the menu has finished opening. */
        this._afterOpened$ = new Subject();
        /** Subject for notifying the user that the menu has finished closing. */
        this._afterClosed$ = new Subject();
        /** Subject for notifying the user that the menu has started closing. */
        this._closeStart$ = new Subject();
        /** Subject for notifying the user that the menu has started opening. */
        this._openStart$ = new Subject();
        this._isOpen = false;
        if (panelClass) {
            _overlayRef.addPanelClass(panelClass);
        }
        this._backdropSub = this._overlayRef
            .backdropClick()
            .subscribe(() => {
            this.close();
        });
        this.router.events.pipe(filter((event) => event instanceof NavigationStart), tap(() => this.destroy()), take(1)).subscribe();
    }
    /**
     * Set reference to menu container
     * @param value
     */
    set containerRef(value) {
        this._externalMenuContainerRef = value;
    }
    /**
     * Set reference to menu component
     * @param value
     */
    set componentRef(value) {
        this._externalMenuComponentRef = value;
    }
    /**
     * Return actual status of the menu
     */
    get isOpen() {
        return this._isOpen;
    }
    /**
     * OverlayRef for possibility to manipulate overlay position at any time
     */
    get overlayRef() {
        return this._overlayRef;
    }
    /**
     * Gets an observable that is notified when the dialog is finished closing.
     */
    afterClosed() {
        return this._afterClosed$.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog is finished opening.
     */
    afterOpened() {
        return this._afterOpened$.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog open starts.
     */
    openStart() {
        return this._openStart$.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog is finished opening.
     */
    closeStart() {
        return this._closeStart$.asObservable();
    }
    /**
     * Open menu and notify observable
     */
    open() {
        new Observable((obs) => {
            setTimeout(() => {
                if (this._openStart$.observers.length) {
                    this._openStart$.next(obs);
                }
                else {
                    obs.next();
                    obs.complete();
                }
            });
        }).subscribe({
            next: () => {
                this._afterOpened$.next();
                this._afterOpened$.complete();
            },
            error: () => {
                this.destroy();
            }
        });
    }
    /**
     * Close the menu.
     * @param result Optional result to return to the dialog opener.
     */
    close(result) {
        new Observable((obs) => {
            if (this._closeStart$.observers.length) {
                this._closeStart$.next(obs);
            }
            else {
                obs.next();
                obs.complete();
            }
        }).subscribe({
            next: () => {
                this._result = result;
                this._afterClosed$.next(result);
                this.destroy();
            }
        });
    }
    destroy() {
        this._overlayRef.detachBackdrop();
        this._overlayRef.detach();
        this._backdropSub.unsubscribe();
        this._externalMenuComponentRef && this._externalMenuComponentRef.destroy();
        this._openStart$.complete();
        this._closeStart$.complete();
        this._afterOpened$.complete();
        this._afterClosed$.complete();
        window.document.body.classList.remove('fs-sidenav-menu-open');
    }
}

class FsSidenavMenuContainerComponent extends BasePortalOutlet {
    constructor() {
        super();
    }
    setExternalMenuRef(value) {
        this.ref = value;
    }
    /**
     * Attach a ComponentPortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw Error('Drawer component already attached');
        }
        return this._portalOutlet.attachComponentPortal(portal);
    }
    /**
     * Attach a TemplatePortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throw Error('Drawer template already attached');
        }
        return this._portalOutlet.attachTemplatePortal(portal);
    }
}
FsSidenavMenuContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavMenuContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsSidenavMenuContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsSidenavMenuContainerComponent, selector: "fs-external-menu", host: { classAttribute: "fs-sidenav-menu-container" }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ng-template cdkPortalOutlet></ng-template>\n", directives: [{ type: i1.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavMenuContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-external-menu',
                    templateUrl: 'sidenav-menu-container.component.html',
                    host: {
                        'class': 'fs-sidenav-menu-container'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }] } });

class ExternalMenuService {
    constructor(_overlay, _injector, _router, _breakpointObserver) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._router = _router;
        this._breakpointObserver = _breakpointObserver;
    }
    create(menu, container, data) {
        const component = menu.component;
        data = data || {};
        const overlayRef = this.createOverlay(container, menu);
        const menuRef = new SidenavMenuRef(overlayRef, this._router, menu.panelClass);
        const containerRef = this.attachContainer(overlayRef, menuRef, data);
        const componentRef = this.attachComponent(component, containerRef, menuRef, data);
        menuRef.containerRef = containerRef;
        containerRef.setExternalMenuRef(menuRef);
        menuRef.componentRef = componentRef;
        window.document.body.classList.add('fs-sidenav-menu-open');
        return menuRef;
    }
    createOverlay(container, menu) {
        const overlayConfig = this.getOverlayConfig(container, menu);
        return this._overlay.create(overlayConfig);
    }
    getOverlayConfig(container, menu) {
        const element = new ElementRef(container); // positionStrategy needs ElementRef;
        let strategy = null;
        if (this._breakpointObserver.isMatched(Breakpoints.XSmall)) {
            strategy = this._overlay
                .position()
                .global()
                .centerHorizontally()
                .centerVertically();
        }
        else {
            const positions = [
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetX: menu.offsetX,
                    offsetY: menu.offsetY
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                }
            ];
            strategy = this._overlay
                .position()
                .flexibleConnectedTo(element)
                .withPositions(positions);
        }
        return new OverlayConfig({
            positionStrategy: strategy,
            scrollStrategy: this._overlay.scrollStrategies.block(),
            hasBackdrop: true,
            disposeOnNavigation: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: 'fs-sidenav-menu-overlay-pane'
        });
    }
    attachContainer(overlayRef, menuRef, data) {
        const injector = this.createInjector(menuRef, data);
        const containerPortal = new ComponentPortal(FsSidenavMenuContainerComponent, undefined, injector);
        const containerRef = overlayRef.attach(containerPortal);
        return containerRef.instance;
    }
    attachComponent(componentRef, externalContainer, externalRef, data) {
        const injector = this.createInjector(externalRef, data);
        return externalContainer.attachComponentPortal(new ComponentPortal(componentRef, undefined, injector));
    }
    createInjector(componentRef, config) {
        const injectionTokens = new WeakMap([
            [SidenavMenuRef, componentRef],
            [MENU_DATA, config]
        ]);
        return new PortalInjector(this._injector, injectionTokens);
    }
}
ExternalMenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: ExternalMenuService, deps: [{ token: i1$1.Overlay }, { token: i0.Injector }, { token: i2.Router }, { token: i3.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
ExternalMenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: ExternalMenuService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: ExternalMenuService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Overlay }, { type: i0.Injector }, { type: i2.Router }, { type: i3.BreakpointObserver }]; } });

var ItemActionType;
(function (ItemActionType) {
    ItemActionType[ItemActionType["Button"] = 0] = "Button";
    ItemActionType[ItemActionType["Menu"] = 1] = "Menu";
})(ItemActionType || (ItemActionType = {}));

class FsSidenavItemDirective {
    constructor(_renderer, _el, _router) {
        this._renderer = _renderer;
        this._el = _el;
        this._router = _router;
        this.childrenItems = [];
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._router.events
            .pipe(takeUntil(this._destroy$))
            .subscribe(event => {
            if (event instanceof NavigationEnd) {
                this._activatePath();
            }
        });
    }
    ngAfterContentInit() {
        this._activatePath();
    }
    ngOnChanges(changes) {
        if (changes.item && !changes.item.firstChange) {
            this._activatePath();
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _activatePath() {
        const active = this._isActiveUrl(this.item.path, this.item.paths, this.childrenItems, this.item.exact, this._router.url);
        if (active) {
            this._renderer.addClass(this._el.nativeElement, this.activeClass);
        }
        else {
            this._renderer.removeClass(this._el.nativeElement, this.activeClass);
        }
    }
    _isActiveUrl(itemPath, itemPaths, childrenItems, itemExact, url) {
        if (itemPath) {
            const currentPath = url.split('?')[0];
            if (itemExact) {
                if (currentPath === itemPath) {
                    return true;
                }
            }
            else if (currentPath.indexOf(itemPath) === 0) {
                return true;
            }
        }
        if ((itemPaths || []).some((path) => {
            return url.match(path);
        })) {
            return true;
        }
        if ((childrenItems || []).some((item) => {
            return this._isActiveUrl(item.path, item.paths, [], item.exact, url);
        })) {
            return true;
        }
        return false;
    }
}
FsSidenavItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavItemDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Directive });
FsSidenavItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsSidenavItemDirective, selector: "[fsSidenavItem]", inputs: { item: "item", childrenItems: "childrenItems", activeClass: "activeClass" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsSidenavItem]',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i2.Router }]; }, propDecorators: { item: [{
                type: Input
            }], childrenItems: [{
                type: Input
            }], activeClass: [{
                type: Input
            }] } });

class FsSidenavSearchPipe {
    transform(navItems, searchName) {
        if (searchName && searchName !== '') {
            return this.filter(navItems, searchName);
        }
        else {
            return navItems;
        }
    }
    filter(items, search) {
        const filtered = [];
        items.forEach(item => {
            if (item.label.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
                filtered.push(item);
            }
            else {
                const childItems = item.items;
                if (childItems) {
                    filtered.push(...this.filter(childItems, search));
                }
            }
        });
        return filtered;
    }
}
FsSidenavSearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavSearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsSidenavSearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavSearchPipe, name: "fsSidenavSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavSearchPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'fsSidenavSearch' }]
        }] });

class FsSidenavComponent {
    constructor(_service, _cdRef) {
        this._service = _service;
        this._cdRef = _cdRef;
        this.click = new EventEmitter();
        this.itemActionType = ItemActionType;
        this.config = { items: [] };
    }
    set setConfig(config) {
        this.config = Object.assign({}, config);
        if (this.config.items) {
            this.config.items.forEach(item => {
                if (item.exact === undefined) {
                    item.exact = true;
                }
            });
        }
    }
    /**
     * Temporary hack
     */
    detectChanges() {
        this._cdRef.markForCheck();
    }
    clickListItem($event, item) {
        this.click.emit({ item: item, $event: $event });
        if (item.click) {
            item.click({ item: item, $event: $event });
        }
    }
    actionButtonClick(event, action) {
        event.stopPropagation();
        event.preventDefault();
        if (action.click) {
            action.click.call(action);
        }
        if (action.url) {
            window.open(action.url, '_blank');
        }
    }
    actionMenuClick($event, action) {
        $event.preventDefault();
        $event.stopPropagation();
        if (action.menu.component) {
            const sourceElement = $event.srcElement;
            this._service.create(action.menu, sourceElement, action.menu.data);
        }
    }
    clickLinkItem($event, item) {
        if (item.menu) {
            const sourceElement = $event.currentTarget;
            this._service.create(item.menu, sourceElement, item.menu.data);
        }
    }
}
FsSidenavComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavComponent, deps: [{ token: ExternalMenuService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsSidenavComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsSidenavComponent, selector: "fs-sidenav", inputs: { setConfig: ["config", "setConfig"], search: "search" }, outputs: { click: "click" }, ngImport: i0, template: "<div class=\"items\">\n  <ng-container *ngTemplateOutlet=\"itemsTemplate; context: { items: config.items | fsSidenavSearch: search, level: 0 }\"></ng-container>\n</div>\n\n<ng-template #itemsTemplate let-items=\"items\" let-level=\"level\">\n  <ul class=\"level-{{level}}\">\n    <ng-container *ngFor=\"let item of items\">\n      <li\n          [ngClass]=\"{ item: true, 'item-menu': item.menu }\"\n          fsSidenavItem\n          activeClass=\"active-item\"\n          [item]=\"item\"\n          [childrenItems]=\"item.items\"\n          [class]=\"item.class\"\n          (click)=\"clickListItem($event,item)\"\n          *ngIf=\"!item.hide\">\n        <a\n            class=\"item-content\"\n            [routerLink]=\"item.path\"\n            fsSidenavItem\n            activeClass=\"active-item-content\"\n            [item]=\"item\"\n            (click)=\"clickLinkItem($event, item)\"\n            *ngIf=\"item.path; else elseLink\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { item: item, level: level }\"></ng-container>\n        </a>\n        <ng-template #elseLink>\n          <div\n              class=\"item-content\"\n              fsSidenavItem\n              activeClass=\"active-content\"\n              [item]=\"item\"\n              (click)=\"clickLinkItem($event, item)\"\n              [ngClass]=\"{ clickable: item.click }\">\n            <ng-container *ngTemplateOutlet=\"itemTemplate; context: { item: item, level: level }\"></ng-container>\n          </div>\n        </ng-template>\n        <ng-container *ngIf=\"item.items && item.items.length\">\n          <ng-container *ngTemplateOutlet=\"itemsTemplate; context: { items: item.items, level: level + 1 }\"></ng-container>\n        </ng-container>\n      </li>\n    </ng-container>\n  </ul>\n</ng-template>\n\n<ng-template #itemTemplate let-item=\"item\" let-level=\"level\">\n  <mat-icon class=\"item-icon\" *ngIf=\"item.icon\">{{item.icon}}</mat-icon>\n\n  <ng-container *ngIf=\"item.template; else labels\">\n    <ng-container *ngTemplateOutlet=\"item.template; context: { item: item }\"></ng-container>\n  </ng-container>\n\n  <ng-template #labels>\n    <img class=\"item-image\" [src]=\"item.image\" *ngIf=\"item.image\">\n    <div class=\"item-labels\">\n      <div class=\"superlabel\" *ngIf=\"item.superlabel\">{{item.superlabel}}</div>\n      <div class=\"label\" *ngIf=\"item.label\">{{item.label}}</div>\n      <div class=\"sublabel\" *ngIf=\"item.sublabel\">{{item.sublabel}}</div>\n    </div>\n  </ng-template>\n\n  <ng-container *ngIf=\"level === 0 && item.expandable && item.items && item.items.length\">\n    <mat-icon class=\"closed\">chevron_right</mat-icon>\n    <mat-icon class=\"open\">keyboard_arrow_down</mat-icon>\n  </ng-container>\n\n  <span class=\"item-actions\">\n    <ng-container *ngFor=\"let action of item.actions\">\n      <ng-container *ngIf=\"action.type==itemActionType.Button\">\n\n        <a mat-icon-button (click)=\"actionButtonClick($event, action)\" [routerLink]=\"action.path\" [queryParams]=\"action.queryParams\" class=\"item-action\" [ngClass]=\"action.class\" *ngIf=\"action.path\">\n          <mat-icon>{{action.icon}}</mat-icon>\n        </a>\n\n        <button mat-icon-button (click)=\"actionButtonClick($event, action)\" class=\"item-action\" *ngIf=\"!action.url && !action.path\" [ngClass]=\"action.class\">\n          <mat-icon>{{ action.icon }}</mat-icon>\n        </button>\n\n        <a mat-icon-button (click)=\"actionButtonClick($event, action)\" class=\"item-action\" [ngClass]=\"action.class\" *ngIf=\"action.url\" [href]=\"action.url\">\n          <mat-icon>{{action.icon}}</mat-icon>\n        </a>\n      </ng-container>\n\n      <button mat-icon-button *ngIf=\"action.type==itemActionType.Menu\" class=\"item-action\" (click)=\"actionMenuClick($event, action)\" [ngClass]=\"action.class\">\n        <mat-icon>{{action.icon}}</mat-icon>\n      </button>\n    </ng-container>\n  </span>\n</ng-template>\n", components: [{ type: i2$1.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i3$1.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }, { type: i3$1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i4.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: FsSidenavItemDirective, selector: "[fsSidenavItem]", inputs: ["item", "childrenItems", "activeClass"] }, { type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["routerLink", "target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo"] }], pipes: { "fsSidenavSearch": FsSidenavSearchPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-sidenav',
                    templateUrl: 'fs-sidenav.component.html',
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: ExternalMenuService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { setConfig: [{
                type: Input,
                args: ['config']
            }], search: [{
                type: Input
            }], click: [{
                type: Output
            }] } });

class SidenavMenuActionsComponent {
}
SidenavMenuActionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SidenavMenuActionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: SidenavMenuActionsComponent, selector: "fs-sidenav-menu-actions", host: { classAttribute: "fs-sidenav-menu-actions" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-sidenav-menu-actions',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'fs-sidenav-menu-actions'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

class SidenavMenuContentComponent {
}
SidenavMenuContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SidenavMenuContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: SidenavMenuContentComponent, selector: "fs-sidenav-menu-content", host: { classAttribute: "overflow-shadow fs-sidenav-menu-content" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-sidenav-menu-content',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'overflow-shadow fs-sidenav-menu-content'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

class SidenavMenuComponent {
}
SidenavMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SidenavMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: SidenavMenuComponent, selector: "fs-sidenav-menu", host: { classAttribute: "fs-sidenav-menu" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-sidenav-menu',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'fs-sidenav-menu'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

class SidenavMenuItemsComponent {
}
SidenavMenuItemsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuItemsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SidenavMenuItemsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: SidenavMenuItemsComponent, selector: "fs-sidenav-menu-items", host: { classAttribute: "fs-sidenav-menu-items" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuItemsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-sidenav-menu-items',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'fs-sidenav-menu-items'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

class SidenavMenuItemComponent {
}
SidenavMenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SidenavMenuItemComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: SidenavMenuItemComponent, selector: "[fs-sidenav-menu-item]", host: { classAttribute: "mat-menu-item fs-sidenav-menu-item" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: SidenavMenuItemComponent, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-sidenav-menu-item]',
                    host: {
                        'class': 'mat-menu-item fs-sidenav-menu-item'
                    }
                }]
        }] });

class FsSidenavModule {
    static forRoot() {
        return {
            ngModule: FsSidenavModule,
            providers: [ExternalMenuService]
        };
    }
}
FsSidenavModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsSidenavModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavModule, declarations: [FsSidenavComponent,
        FsSidenavSearchPipe,
        FsSidenavMenuContainerComponent,
        SidenavMenuActionsComponent,
        SidenavMenuContentComponent,
        SidenavMenuComponent,
        SidenavMenuItemComponent,
        SidenavMenuItemsComponent,
        FsSidenavItemDirective], imports: [CommonModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        PortalModule,
        FsScrollModule], exports: [FsSidenavComponent,
        FsSidenavMenuContainerComponent,
        SidenavMenuActionsComponent,
        SidenavMenuContentComponent,
        SidenavMenuComponent,
        SidenavMenuItemsComponent,
        SidenavMenuItemComponent] });
FsSidenavModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavModule, providers: [
        FsSidenavSearchPipe,
        ExternalMenuService
    ], imports: [[
            CommonModule,
            RouterModule,
            MatIconModule,
            MatButtonModule,
            MatMenuModule,
            PortalModule,
            FsScrollModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsSidenavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        MatIconModule,
                        MatButtonModule,
                        MatMenuModule,
                        PortalModule,
                        FsScrollModule
                    ],
                    exports: [
                        FsSidenavComponent,
                        FsSidenavMenuContainerComponent,
                        SidenavMenuActionsComponent,
                        SidenavMenuContentComponent,
                        SidenavMenuComponent,
                        SidenavMenuItemsComponent,
                        SidenavMenuItemComponent
                    ],
                    declarations: [
                        FsSidenavComponent,
                        FsSidenavSearchPipe,
                        FsSidenavMenuContainerComponent,
                        SidenavMenuActionsComponent,
                        SidenavMenuContentComponent,
                        SidenavMenuComponent,
                        SidenavMenuItemComponent,
                        SidenavMenuItemsComponent,
                        FsSidenavItemDirective,
                    ],
                    providers: [
                        FsSidenavSearchPipe,
                        ExternalMenuService
                    ],
                }]
        }] });

/*
 * Public API
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ExternalMenuService, FsSidenavComponent, FsSidenavMenuContainerComponent, FsSidenavModule, ItemActionType, MENU_DATA, SidenavMenuActionsComponent, SidenavMenuComponent, SidenavMenuContentComponent, SidenavMenuItemComponent, SidenavMenuItemsComponent, SidenavMenuRef };
